import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../services/api';
import { Search, Filter, ArrowUpDown, ChevronUp, ChevronDown, Download, RefreshCw } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { useSortableData } from '../hooks/useSortableData';
import Pagination from './Pagination';
const InventoryTable = () => {
    const { user } = useAuth();
    const isWarehouseRestricted = user && (user.role === 'manager' || user.role === 'picker') && user.warehouse;
    const [inventory, setInventory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isFetching, setIsFetching] = useState(false);
    const [syncStatus, setSyncStatus] = useState({ status: 'idle' });

    // Filter States
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedClient, setSelectedClient] = useState('All');
    const [selectedWarehouse, setSelectedWarehouse] = useState(isWarehouseRestricted ? user.warehouse : 'All');
    const [selectedStatus, setSelectedStatus] = useState('Healthy');
    const [selectedActiveFilter, setSelectedActiveFilter] = useState('Active');

    // Pagination States
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);

    const fetchInventory = async () => {
        try {
            const res = await api.inventory.getCurrent().catch(() => null);
            if (res?.data?.data?.length > 0) {
                setInventory(res.data.data);
            } else {
                setInventory([]);
            }
        } catch (error) {
            console.error("Inventory error", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInventory();
        
        const checkStatus = async () => {
            try {
                const res = await api.inventory.getSyncStatus();
                setSyncStatus(res.data);
            } catch (err) {
                console.error("Failed to check sync status", err);
            }
        };
        checkStatus();
    }, []);

    // Polling for sync status
    useEffect(() => {
        let interval;
        if (syncStatus.status === 'in_progress') {
            interval = setInterval(async () => {
                try {
                    const res = await api.inventory.getSyncStatus();
                    setSyncStatus(res.data);
                    
                    if (res.data.status === 'completed') {
                        alert(`Successfully synced! Scanned ${res.data.processedCount} total products and found ${res.data.kitCount} kit products.`);
                        fetchInventory();
                        clearInterval(interval);
                    }
                    else if (res.data.status === 'failed') {
                        alert(`Background sync failed: ${res.data.error}`);
                        clearInterval(interval);
                    } else if (res.data.status === 'cancelled') {
                        alert("Sync stopped by user.");
                        setSyncStatus({ status: 'idle' });
                        clearInterval(interval);
                    }
                } catch (err) {
                    console.error("Polling error", err);
                }
            }, 5000);
        }
        return () => clearInterval(interval);
    }, [syncStatus.status]);

    const handleFetchManual = async () => {
        if (syncStatus.status === 'in_progress') return;
        
        setIsFetching(true);
        try {
            await api.inventory.fetchManual('all');
            setSyncStatus({ status: 'in_progress' });
            alert("Background inventory sync started. You can continue working; you'll be notified when it's done.");
        } catch (error) {
            console.error("Manual fetch error", error);
            const msg = error.response?.data?.message || "Failed to start inventory sync.";
            alert(msg);
        } finally {
            setIsFetching(false);
        }
    };

    const handleStopSync = async () => {
        if (!window.confirm("Are you sure you want to stop the current synchronization?")) return;
        try {
            await api.inventory.stopSync();
            setSyncStatus({ status: 'cancelled' });
            alert("Sync cancellation requested.");
        } catch (error) {
            console.error("Stop sync error", error);
            alert("Failed to stop synchronization.");
        }
    };


    // Compute unique warehouses for the filter dropdown
    const uniqueWarehouses = useMemo(() => {
        const warehouses = new Set(inventory.map(item => item.warehouse_name));
        return ['All', ...Array.from(warehouses)];
    }, [inventory]);

    // Compute unique clients and their SKU counts
    const { uniqueClients, clientCounts } = useMemo(() => {
        const counts = {};
        inventory.forEach(item => {
            if (item.clientName) {
                counts[item.clientName] = (counts[item.clientName] || 0) + 1;
            }
        });
        const unique = ['All', ...Object.keys(counts).sort()];
        return { uniqueClients: unique, clientCounts: counts };
    }, [inventory]);

    // Apply filters to data
    const filteredInventory = useMemo(() => {
        return inventory.filter(item => {
            const matchesSearch = item.sku.toLowerCase().startsWith(searchTerm.toLowerCase()) ||
                item.product_name.toLowerCase().startsWith(searchTerm.toLowerCase());
            const matchesWarehouse = selectedWarehouse === 'All' || item.warehouse_name === selectedWarehouse;
            const matchesClient = selectedClient === 'All' || item.clientName === selectedClient;

            let matchesStatus = true;
            if (selectedStatus !== 'All') {
                if (selectedStatus === 'Healthy') matchesStatus = item.sellable > 100;
                else if (selectedStatus === 'Low Stock') matchesStatus = item.sellable > 0 && item.sellable <= 100;
                else if (selectedStatus === 'Out of Stock') matchesStatus = item.sellable <= 0;
            }

            let matchesActive = true;
            if (selectedActiveFilter !== 'All') {
                matchesActive = selectedActiveFilter === 'Active' ? item.active : !item.active;
            }

            return matchesSearch && matchesWarehouse && matchesClient && matchesStatus && matchesActive;
        });
    }, [inventory, searchTerm, selectedWarehouse, selectedClient, selectedStatus, selectedActiveFilter]);

    const { items: sortedInventory, requestSort, sortConfig } = useSortableData(filteredInventory);

    const totalPages = Math.ceil(sortedInventory.length / itemsPerPage);
    const paginatedInventory = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return sortedInventory.slice(start, start + itemsPerPage);
    }, [sortedInventory, currentPage, itemsPerPage]);

    const exportToCSV = () => {
        const headers = ['Client', 'SKU', 'Status', 'Product Name', 'Warehouse', 'On Hand', 'Allocated', 'Sellable'];
        const rows = sortedInventory.map(item => [
            `"${item.clientName || ''}"`,
            item.sku,
            item.active ? 'Active' : 'Inactive',
            `"${item.product_name.replace(/"/g, '""')}"`,
            item.warehouse_name,
            item.on_hand,
            item.allocated,
            item.sellable
        ]);

        const csvContent = headers.join(",") + "\n" + rows.map(e => e.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `Invecta_inventory_records_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const getSortIcon = (key) => {
        if (!sortConfig || sortConfig.key !== key) return <ArrowUpDown size={14} style={{ display: 'inline', marginLeft: '4px', opacity: 0.3 }} />;
        return sortConfig.direction === 'ascending' ? <ChevronUp size={14} style={{ display: 'inline', marginLeft: '4px', color: 'var(--primary)' }} /> : <ChevronDown size={14} style={{ display: 'inline', marginLeft: '4px', color: 'var(--primary)' }} />;
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedClient, selectedWarehouse, selectedStatus, selectedActiveFilter]);

    return (
        <div>
            <div className="page-header" style={{ marginBottom: '1.5rem' }}>
                <div>
                    <h1>Inventory Records</h1>
                    <p>Detailed view of current stock status across all locations.</p>
                </div>
                {user?.role === 'admin' && (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                            onClick={handleFetchManual}
                            disabled={isFetching || syncStatus.status === 'in_progress'}
                            className="settings-add-btn"
                            style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '0.5rem', 
                                opacity: (isFetching || syncStatus.status === 'in_progress') ? 0.7 : 1,
                                cursor: (isFetching || syncStatus.status === 'in_progress') ? 'not-allowed' : 'pointer'
                            }}
                        >
                            <RefreshCw size={18} className={(isFetching || syncStatus.status === 'in_progress') ? "spin" : ""} />
                             {syncStatus.status === 'in_progress' ? (syncStatus.syncType === 'kits' ? "Syncing Kits..." : "Syncing Inventory...") : (isFetching ? "Starting..." : "Sync Inventory")}
                        </button>
                        <button
                            onClick={exportToCSV}
                            className="settings-add-btn"
                            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                        >
                            <Download size={18} /> Export CSV
                        </button>
                    </div>
                )}
            </div>

            {syncStatus.status === 'in_progress' && (
                <div style={{ 
                    backgroundColor: '#e0f2fe', 
                    border: '1px solid #7dd3fc', 
                    borderRadius: '8px', 
                    padding: '0.75rem 1rem', 
                    marginBottom: '1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <RefreshCw size={18} className="spin" color="#0369a1" />
                        <span style={{ color: '#0369a1', fontWeight: '500' }}>
                            {syncStatus.syncType === 'kits' ? "Syncing Kit Products... " : "Syncing Full Inventory... "}
                            {syncStatus.processedCount !== undefined ? (syncStatus.syncType === 'kits' ? `Found ${syncStatus.kitCount || 0} kits so far.` : `Updated ${syncStatus.processedCount} items so far.`) : ''}
                        </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{ fontSize: '0.875rem', color: '#0369a1', opacity: 0.8 }}>
                            This runs in the background. You can continue working.
                        </div>
                        <button 
                            onClick={handleStopSync}
                            style={{
                                backgroundColor: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '0.4rem 0.8rem',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >
                            Stop Sync
                        </button>
                    </div>
                </div>
            )}

            <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem 1.5rem' }}>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1', minWidth: '250px', position: 'relative' }}>
                        <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                        <input
                            type="text"
                            placeholder="Search by SKU or Product Name..."
                            value={searchTerm}
                            onChange={(e) => {
                                const val = e.target.value;
                                setSearchTerm(val);
                                if (val.trim() !== '') {
                                    setSelectedStatus('All');
                                } else {
                                    setSelectedStatus('Healthy');
                                }
                            }}
                            style={{
                                width: '100%', padding: '0.6rem 1rem 0.6rem 2.5rem',
                                borderRadius: '6px', border: '1px solid #e2e8f0',
                                outline: 'none', fontFamily: 'inherit'
                            }}
                        />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '200px' }}>
                        <Filter size={18} color="var(--text-muted)" />
                        <select
                            value={selectedClient}
                            onChange={(e) => setSelectedClient(e.target.value)}
                            style={{
                                width: '100%', padding: '0.6rem 1rem',
                                borderRadius: '6px', border: '1px solid #e2e8f0',
                                outline: 'none', fontFamily: 'inherit',
                                backgroundColor: 'white'
                            }}
                        >
                            {uniqueClients.map(c => (
                                <option key={c} value={c}>
                                    {c === 'All' ? 'All Clients' : `${c} (${clientCounts[c]})`}
                                </option>
                            ))}
                        </select>
                        <select
                            value={selectedActiveFilter}
                            onChange={(e) => setSelectedActiveFilter(e.target.value)}
                            style={{
                                width: '100%', padding: '0.6rem 1rem',
                                borderRadius: '6px', border: '1px solid #e2e8f0',
                                outline: 'none', fontFamily: 'inherit',
                                backgroundColor: 'white',
                                marginLeft: '0.5rem'
                            }}
                        >
                            <option value="All">All Items</option>
                            <option value="Active">Active Only</option>
                            <option value="Inactive">Inactive Only</option>
                        </select>
                        <select
                            value={selectedWarehouse}
                            onChange={(e) => !isWarehouseRestricted && setSelectedWarehouse(e.target.value)}
                            disabled={isWarehouseRestricted}
                            style={{
                                width: '100%', padding: '0.6rem 1rem',
                                borderRadius: '6px', border: '1px solid #e2e8f0',
                                outline: 'none', fontFamily: 'inherit',
                                backgroundColor: isWarehouseRestricted ? '#f1f5f9' : 'white',
                                marginLeft: '0.5rem',
                                opacity: isWarehouseRestricted ? 0.8 : 1
                            }}
                        >
                            {isWarehouseRestricted ? (
                                <option value={user.warehouse}>{user.warehouse}</option>
                            ) : (
                                uniqueWarehouses.map(wh => (
                                    <option key={wh} value={wh}>{wh === 'All' ? 'All Warehouses' : wh}</option>
                                ))
                            )}
                        </select>
                        <select
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            style={{
                                width: '100%', padding: '0.6rem 1rem',
                                borderRadius: '6px', border: '1px solid #e2e8f0',
                                outline: 'none', fontFamily: 'inherit',
                                backgroundColor: 'white',
                                marginLeft: '0.5rem'
                            }}
                        >
                            <option value="All">All Statuses</option>
                            <option value="Healthy">Healthy</option>
                            <option value="Low Stock">Low Stock</option>
                            <option value="Out of Stock">Out of Stock</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="card table-container">
                {loading ? (
                    <p>Loading inventory data...</p>
                ) : (
                    <table className="responsive-table">
                        <thead>
                            <tr>
                                <th className="sortable" onClick={() => requestSort('clientName')}>Client {getSortIcon('clientName')}</th>
                                <th className="sortable" onClick={() => requestSort('sku')}>SKU {getSortIcon('sku')}</th>
                                <th className="sortable" onClick={() => requestSort('active')}>Status {getSortIcon('active')}</th>
                                <th className="sortable" onClick={() => requestSort('product_name')}>Product Name {getSortIcon('product_name')}</th>
                                <th className="sortable" onClick={() => requestSort('warehouse_name')}>Warehouse {getSortIcon('warehouse_name')}</th>
                                <th className="sortable" onClick={() => requestSort('on_hand')}>On Hand {getSortIcon('on_hand')}</th>
                                <th className="sortable" onClick={() => requestSort('allocated')}>Allocated {getSortIcon('allocated')}</th>
                                <th className="sortable" onClick={() => requestSort('sellable')}>Sellable {getSortIcon('sellable')}</th>
                                <th className="sortable" onClick={() => requestSort('sellable')}>Status {getSortIcon('sellable')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedInventory.length === 0 ? (
                                <tr>
                                    <td colSpan="8" style={{ textAlign: 'center', padding: '4rem 2rem', color: 'var(--text-muted)' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                                            <Search size={48} style={{ opacity: 0.2 }} />
                                            <div>
                                                <p style={{ fontWeight: 500, color: 'var(--text-main)', marginBottom: '0.25rem' }}>No inventory records found</p>
                                                <p style={{ fontSize: '0.875rem' }}>We couldn't find any items matching your filters, or the database is currently empty.</p>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                paginatedInventory.map((item) => (
                                    <tr key={item.id}>
                                        <td data-label="Client" style={{ fontWeight: 600, color: 'var(--primary)' }} title={item.clientName}>{item.clientName}</td>
                                        <td data-label="SKU" style={{ fontWeight: 600, color: 'var(--text-main)' }} title={item.sku}>{item.sku}</td>
                                        <td data-label="Status">
                                            {item.active ? (
                                                <span className="badge success">Active</span>
                                            ) : (
                                                <span className="badge inactive">Inactive</span>
                                            )}
                                        </td>
                                        <td data-label="Product Name" title={item.product_name}>{item.product_name}</td>
                                        <td data-label="Warehouse" title={item.warehouse_name}>{item.warehouse_name}</td>
                                        <td data-label="On Hand">{item.on_hand}</td>
                                        <td data-label="Allocated">{item.allocated}</td>
                                        <td data-label="Sellable">{item.sellable}</td>
                                        <td data-label="Status">
                                            {item.sellable > 100 ? (
                                                <span className="badge success">Healthy</span>
                                            ) : item.sellable > 0 ? (
                                                <span className="badge low_stock">Low Stock</span>
                                            ) : (
                                                <span className="badge out_of_stock">Out of Stock</span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                )}

                {!loading && filteredInventory.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', backgroundColor: 'white', borderTop: '1px solid #e2e8f0', borderRadius: '0 0 8px 8px', flexWrap: 'wrap', gap: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span>Rows per page:</span>
                                <select
                                    value={itemsPerPage}
                                    onChange={(e) => {
                                        setItemsPerPage(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                    style={{
                                        padding: '0.25rem 0.5rem',
                                        borderRadius: '4px',
                                        border: '1px solid #e2e8f0',
                                        outline: 'none',
                                        fontFamily: 'inherit',
                                        backgroundColor: 'white'
                                    }}
                                >
                                    {[10, 25, 50, 75, 100, 250, 500, 1000, 5000, 10000].map(val => (
                                        <option key={val} value={val}>{val}</option>
                                    ))}
                                </select>
                            </div>
                            <span>Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredInventory.length)} of {filteredInventory.length} entries</span>
                        </div>
                        <Pagination currentPage={currentPage} totalPages={totalPages} setCurrentPage={setCurrentPage} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default InventoryTable;
